import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LayoutDashboard, Building2, ClipboardList, LogOut, Home, Key } from 'lucide-react';
import { useState } from 'react';
import { changePassword } from '../../utils/api';
import toast from 'react-hot-toast';

function ChangePasswordModal({ onClose }) {
  const [curr, setCurr] = useState('');
  const [next, setNext] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error('New password must be 8+ characters');
    setLoading(true);
    try {
      await changePassword(curr, next);
      toast.success('Password changed!');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-head"><h3>Change Password</h3><button className="modal-close" onClick={onClose}>✕</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Current Password</label>
              <input type="password" className="form-input" value={curr} onChange={e => setCurr(e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label">New Password (min 8 chars)</label>
              <input type="password" className="form-input" value={next} onChange={e => setNext(e.target.value)} required minLength={8} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-green btn-sm" disabled={loading}>
              {loading ? 'Saving...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const NAV = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/projects', label: 'Projects', icon: Building2 },
  { to: '/admin/audit', label: 'Audit Log', icon: ClipboardList },
];

export default function AdminLayout() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [showChangePwd, setShowChangePwd] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <aside style={{
        width: 240, background: 'linear-gradient(180deg, #1D9E75 0%, #0F6E56 100%)',
        color: '#fff', display: 'flex', flexDirection: 'column',
        position: 'sticky', top: 0, height: '100vh', flexShrink: 0,
      }}>
        {/* Brand */}
        <div style={{ padding: '24px 20px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 38, height: 38, background: 'rgba(255,255,255,0.2)',
              borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 800, fontSize: 12, border: '1.5px solid rgba(255,255,255,0.3)'
            }}>SRG</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>SRG Real Estate</div>
              <div style={{ fontSize: 11, opacity: 0.75 }}>Admin Panel</div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '8px 12px' }}>
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              borderRadius: 10, textDecoration: 'none', color: '#fff', fontWeight: isActive ? 600 : 400,
              background: isActive ? 'rgba(255,255,255,0.2)' : 'transparent',
              marginBottom: 2, transition: 'all 0.15s', fontSize: 14,
            })}
              onMouseEnter={e => { if (!e.currentTarget.className.includes('active')) e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
              onMouseLeave={e => { if (!e.currentTarget.className.includes('active')) e.currentTarget.style.background = 'transparent'; }}
            >
              <Icon size={17} opacity={0.9} />
              {label}
            </NavLink>
          ))}

          <div style={{ margin: '8px 0', height: 1, background: 'rgba(255,255,255,0.15)' }} />

          <a href="/" target="_blank" style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
            borderRadius: 10, textDecoration: 'none', color: 'rgba(255,255,255,0.8)',
            fontSize: 14, transition: 'all 0.15s',
          }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            <Home size={17} opacity={0.8} /> View Dashboard
          </a>
        </nav>

        {/* User */}
        <div style={{ padding: '16px 12px', borderTop: '1px solid rgba(255,255,255,0.15)' }}>
          <div style={{ padding: '10px 12px', background: 'rgba(255,255,255,0.1)', borderRadius: 10, marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{admin?.name}</div>
            <div style={{ fontSize: 11, opacity: 0.75, textTransform: 'capitalize' }}>{admin?.role}</div>
          </div>
          <button onClick={() => setShowChangePwd(true)} className="btn btn-ghost" style={{
            width: '100%', color: 'rgba(255,255,255,0.8)', gap: 8, justifyContent: 'flex-start',
            padding: '8px 12px', fontSize: 13, marginBottom: 4
          }}>
            <Key size={15} /> Change Password
          </button>
          <button onClick={handleLogout} className="btn btn-ghost" style={{
            width: '100%', color: 'rgba(255,255,255,0.8)', gap: 8, justifyContent: 'flex-start',
            padding: '8px 12px', fontSize: 13,
          }}>
            <LogOut size={15} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main style={{ flex: 1, background: 'var(--bg)', minHeight: '100vh', overflow: 'auto' }}>
        <Outlet />
      </main>

      {showChangePwd && <ChangePasswordModal onClose={() => setShowChangePwd(false)} />}
    </div>
  );
}
